// Tour steps for ReportPage — shown once to new users

export interface TourStep {
  target: string          // data-tour attribute value
  titulo: string
  descripcion: string
  posicion: 'top' | 'bottom' | 'left' | 'right'
}

export const REPORT_TOUR_STEPS: TourStep[] = [
  {
    target: 'borrador-panel',
    titulo: 'RadCore AI en el canvas',
    descripcion:
      'Este panel inferior es la entrada unica: escribe o dicta libremente y RadCore decide si carga patron, genera reporte o edita el contenido actual.',
    posicion: 'top',
  },
  {
    target: 'mic-button',
    titulo: 'Dictado para RadCore AI',
    descripcion:
      'Toca el microfono del panel inferior para dictar una instruccion o un estudio completo. La transcripcion queda visible antes de enviarla.',
    posicion: 'top',
  },
  {
    target: 'borrador-actions',
    titulo: 'Enviar a RadCore',
    descripcion:
      'Enviar a RadCore aplica el resultado directamente al reporte: carga patrones, genera informes o edita el texto segun el contexto.',
    posicion: 'top',
  },
  {
    target: 'editor-panel',
    titulo: 'Canvas del reporte',
    descripcion:
      'Este es el reporte final editable. Los cambios generados por IA aparecen aqui y puedes corregirlos manualmente en cualquier momento.',
    posicion: 'left',
  },
  {
    target: 'report-direct-dictation',
    titulo: 'Dictado directo',
    descripcion:
      'Usa este microfono si quieres insertar dictado directamente en el reporte, sin pasar por la entrada de RadCore AI.',
    posicion: 'bottom',
  },
  {
    target: 'pattern-selector',
    titulo: 'Contexto y patron',
    descripcion:
      'El selector muestra el patron activo y la modalidad que RadCore usa como contexto para generar o editar.',
    posicion: 'bottom',
  },
  {
    target: 'report-action-buttons',
    titulo: 'Acciones del reporte',
    descripcion:
      '"Copiar" envia el reporte al portapapeles para pegar en tu sistema. "Guardar en progreso" lo guarda como borrador. "Terminar y nuevo" lo marca como listo y abre uno nuevo.',
    posicion: 'top',
  },
]

export const SIDEBAR_TOUR_STEPS: TourStep[] = [
  {
    target: 'sidebar-nuevo-reporte',
    titulo: 'Nuevo reporte',
    descripcion:
      'Tu punto de partida. Abre el editor de reportes para dictar hallazgos y generar un informe estructurado con IA.',
    posicion: 'right',
  },
  {
    target: 'sidebar-historial',
    titulo: 'Historial',
    descripcion:
      'Todos tus reportes guardados, organizados por fecha. Busca, revisa y reutiliza informes anteriores.',
    posicion: 'right',
  },
  {
    target: 'sidebar-casos',
    titulo: 'Casos',
    descripcion:
      'Organiza estudios por paciente o caso clinico. Ideal para seguimientos y segundas lecturas.',
    posicion: 'right',
  },
  {
    target: 'sidebar-patrones',
    titulo: 'Patrones',
    descripcion:
      'Tu biblioteca de plantillas. Crea patrones para hallazgos frecuentes y usalos al dictar para acelerar tu flujo.',
    posicion: 'right',
  },
  {
    target: 'sidebar-chat',
    titulo: 'RadCore AI',
    descripcion:
      'Chat clinico con IA. Consulta diagnostico diferencial, dosis de contraste, criterios de clasificacion y mas.',
    posicion: 'right',
  },
  {
    target: 'sidebar-rendimiento',
    titulo: 'Rendimiento',
    descripcion:
      'Estadisticas de tu productividad: reportes por dia, tiempo promedio, tendencias. Mide cuanto tiempo ahorras.',
    posicion: 'right',
  },
  {
    target: 'sidebar-clasificaciones',
    titulo: 'Clasificaciones',
    descripcion:
      'Calculadoras interactivas de BI-RADS, TI-RADS, PI-RADS y LI-RADS con criterios actualizados.',
    posicion: 'right',
  },
  {
    target: 'sidebar-estilo',
    titulo: 'Mi Estilo',
    descripcion:
      'La IA aprende tu forma de escribir reportes. Sube ejemplos y los futuros informes se adaptaran a tu vocabulario y estructura.',
    posicion: 'right',
  },
  {
    target: 'sidebar-ajustes',
    titulo: 'Ajustes',
    descripcion:
      'Configura tu cuenta, cambia contrasena, ajusta el tema visual y gestiona tu plan.',
    posicion: 'right',
  },
]

const SIDEBAR_TOUR_STORAGE_KEY = 'radcore_sidebar_tour_completed'
export const REPORT_TOUR_COMPLETED_EVENT = 'radcore_report_tour_completed'

export function isSidebarTourCompleted(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_TOUR_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function markSidebarTourCompleted(): void {
  try {
    localStorage.setItem(SIDEBAR_TOUR_STORAGE_KEY, '1')
  } catch {
    // localStorage may be unavailable
  }
}

export function resetSidebarTourCompleted(): void {
  try {
    localStorage.removeItem(SIDEBAR_TOUR_STORAGE_KEY)
  } catch {
    // localStorage may be unavailable
  }
}

const TOUR_STORAGE_KEY = 'radcore_report_tour_completed'

export function isTourCompleted(): boolean {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function markTourCompleted(): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, '1')
    window.dispatchEvent(new Event(REPORT_TOUR_COMPLETED_EVENT))
  } catch {
    // localStorage may be unavailable
  }
}

export function resetTourCompleted(): void {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY)
  } catch {
    // localStorage may be unavailable
  }
}

export function canStartSidebarTour(): boolean {
  return isTourCompleted() && !isSidebarTourCompleted()
}
